'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';

import { getAuthStorage } from '@/lib/authStorage';
// Colyseus временно отключён (требует proxy_wstunnel в Apache, см. APACHE_CONFIG_FIX.md).
// import MysteryRoomColyseusBeta from './MysteryRoomColyseusBeta';
// import { mrColyseusEnabled } from './runtime/net/colyseusClient';
import MysteryRoomCoreSlice from './MysteryRoomCoreSlice';
import MysteryRoomP2PBeta from './MysteryRoomP2PBeta';
import MysteryRoomMainMenu from './MysteryRoomMainMenu';
import MysteryRoomMapCard from './MysteryRoomMapCard';
import MysteryRoomMapWorkshop from './map-editor/MysteryRoomMapWorkshop';
import {
  mrApiErrorMessage,
  mrApiFriends,
  mrApiInviteFriend,
  mrApiMapGet,
  mrApiMapsMine,
  mrApiMapsPublished,
  mrApiProfile,
  mrApiStats,
  type MrFriend,
  type MrMapSummary,
  type MrProfileResponse,
  type MrLobbyResponse,
  type MrStatsPlayer,
} from './runtime/net/mysteryRoomApi';
import {
  findMysteryRoomMapDefinition,
  getBuiltinMysteryRoomMapDefinition,
  getBuiltinMysteryRoomMapDefinitions,
  registerMysteryRoomMapDefinition,
  resolveMysteryRoomMapSelection,
} from './runtime/world/mapRegistry';
import type { MysteryRoomMapDefinition } from './runtime/world/mapTypes';
import { parseMysteryRoomP2pMatchIdFromUserInput } from './p2pLobbyInvite';
import {
  MYSTERY_ROOM_DEFAULT_WANDER_BOTS,
  clampWanderBotCount,
} from './mysteryRoomWanderDefaults';
import { formatMysteryRoomPlayableModesShort } from './mysteryRoomMapModes';
import { MR_P2P_SOFT_UNMOUNT_KEY } from './mrP2pSessionFlags';
import type { MysteryRoomBotDifficulty, MysteryRoomGameMode, MysteryRoomGlobalMissionKey } from './runtime/core/localRuntime';

type Props = {
  challenge: {
    seed: string;
    target?: number;
    from?: string;
  };
};

const BUILTIN_MAP_ID = 'lisad_9_night_shift';

function parseNonNegativeIntParam(value: string | null): number | null {
  const parsed = Number.parseInt(String(value ?? ''), 10);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

function parseBooleanParam(value: string | null): boolean {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on';
}

function parseGameModeParam(value: string | null): MysteryRoomGameMode | undefined {
  return value === 'coop' || value === 'survival' || value === 'stealth' || value === 'infection' ? value : undefined;
}

function parseBotDifficultyParam(value: string | null): MysteryRoomBotDifficulty | undefined {
  return value === 'low' || value === 'mid' || value === 'high' ? value : undefined;
}

function parseGlobalMissionParam(value: string | null): MysteryRoomGlobalMissionKey | undefined {
  return value === 'evac' || value === 'last_voice' || value === 'cleanup' ? value : undefined;
}

type MrLobbySnapshotRow = NonNullable<MrLobbyResponse['lobby']>;

type JoinOrStartP2PMenuOverrides = {
  mapId?: string;
  bots?: number;
  botsOnly?: boolean;
  gameMode?: MysteryRoomGameMode;
  botDifficulty?: MysteryRoomBotDifficulty;
  globalMission?: MysteryRoomGlobalMissionKey;
  zombieWaveCount?: number;
  zombieWaveSize?: number;
};

function mrLobbyApiGameModeForPlay(mode: unknown): MysteryRoomGameMode | undefined {
  if (mode === 'incident_drill') return 'survival';
  if (mode === 'sabotage') return 'stealth';
  if (mode === 'classic' || mode === 'training') return 'coop';
  return undefined;
}

function mrLobbyApiBotDifficultyForPlay(d: unknown): MysteryRoomBotDifficulty | undefined {
  if (d === 'easy') return 'low';
  if (d === 'hard') return 'high';
  if (d === 'normal') return 'mid';
  return undefined;
}

function syncedMrLobbyToJoinOverrides(s: MrLobbySnapshotRow): JoinOrStartP2PMenuOverrides {
  const bots = s.players.filter((p) => p.isBot).length;
  const openSlots = (s.slots ?? []).filter((slot) => slot.status === 'open' && !slot.player).length;
  const humans = s.players.filter((p) => !p.isBot).length;
  return {
    mapId: s.mapId ?? undefined,
    bots,
    botsOnly: openSlots === 0 && bots > 0 && humans <= 1,
    gameMode: mrLobbyApiGameModeForPlay(s.settings?.gameMode),
    botDifficulty: mrLobbyApiBotDifficultyForPlay(s.settings?.botDifficulty),
  };
}

// Зачем: оставлено для возможного возврата к Colyseus (когда поднимут proxy_wstunnel в Apache).
// function generateColyseusMatchId(): number {
//   const nowPart = Date.now() % 1_000_000_000;
//   const randomPart = Math.floor(Math.random() * 10_000);
//   return nowPart * 10_000 + randomPart + 1;
// }

export function shouldWaitForRequestedMysteryRoomMap(awaitingRequestedMap: boolean) {
  return awaitingRequestedMap;
}

export default function MysteryRoomMapGate({ challenge }: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const modeParam = searchParams.get('mode');
  const netParam = searchParams.get('net');
  const runtimeParam = searchParams.get('runtime');
  const editorMode = modeParam === 'editor' || searchParams.get('editor') === '1';
  const lobbyTabRaw = (searchParams.get('lobbyTab') ?? '').trim().toLowerCase();
  const lobbyActionRaw = (searchParams.get('lobbyAction') ?? '').trim().toLowerCase();
  /** mode=join устарел: тот же shell, что и создание лобби, вкладка «Поиск игры» */
  const menuView: 'home' | 'create' | 'about' =
    modeParam === 'create' || modeParam === 'join' ? 'create' : modeParam === 'about' ? 'about' : 'home';
  const initialLobbyNavTab: 'create' | 'find' =
    modeParam === 'join' || lobbyTabRaw === 'find' ? 'find' : 'create';
  const currentLobbyMode: 'any' | 'owned' = lobbyActionRaw === 'create' ? 'owned' : 'any';
  const playMode = modeParam === 'play' || modeParam === 'online' || searchParams.get('play') === '1';
  const onlineMode = modeParam === 'online' || netParam === 'p2p' || netParam === 'colyseus' || runtimeParam === 'server';
  // Зачем: для P2P matchId — это lobbyId-строка; Colyseus временно отключён.
  const requestedMatchIdRaw = (searchParams.get('matchId') ?? '').trim();
  const requestedBotCount = parseNonNegativeIntParam(searchParams.get('bots'));
  const [wanderBotsPreference, setWanderBotsPreference] = useState(MYSTERY_ROOM_DEFAULT_WANDER_BOTS);
  useEffect(() => {
    const p = parseNonNegativeIntParam(searchParams.get('bots'));
    if (p !== null) setWanderBotsPreference(clampWanderBotCount(p));
  }, [searchParams]);
  const wanderBotCount = requestedBotCount !== null ? requestedBotCount : wanderBotsPreference;
  const requestedBotsOnly = parseBooleanParam(searchParams.get('botsOnly'));
  const requestedGameMode = parseGameModeParam(searchParams.get('gameMode'));
  const requestedBotDifficulty = parseBotDifficultyParam(searchParams.get('botDifficulty'));
  const requestedGlobalMission = parseGlobalMissionParam(searchParams.get('globalMission'));
  const requestedZombieWaveCount = parseNonNegativeIntParam(searchParams.get('waveCount'));
  const requestedZombieWaveSize = parseNonNegativeIntParam(searchParams.get('waveSize'));
  /** Зачем: хост из MR-лобби первым создаёт P2P с id = lobbyCode (?p2pHost=1). */
  const assumeP2pHostBootstrap = parseBooleanParam(searchParams.get('p2pHost'));
  const editorPreviewMode = searchParams.get('editorPreview') === '1';
  const requestedMapId = searchParams.get('mapId');
  const initialSelection = resolveMysteryRoomMapSelection(requestedMapId);
  const [publishedMaps, setPublishedMaps] = useState<MrMapSummary[]>([]);
  const [myMaps, setMyMaps] = useState<MrMapSummary[]>([]);
  const [profile, setProfile] = useState<MrProfileResponse['profile'] | null>(null);
  const [leaderboard, setLeaderboard] = useState<MrStatsPlayer[]>([]);
  const [profileStatus, setProfileStatus] = useState('Войдите, чтобы видеть деньги, рейтинг и статистику.');
  const [myMapsStatus, setMyMapsStatus] = useState('Войдите, чтобы видеть свои карты.');
  const [friends, setFriends] = useState<MrFriend[]>([]);
  const [friendsStatus, setFriendsStatus] = useState('Войдите, чтобы пригласить друзей в смену.');
  const [inviteStatusByFriendId, setInviteStatusByFriendId] = useState<Record<number, string>>({});
  const [selectedMapId, setSelectedMapId] = useState(initialSelection.id);
  const [selectedMapTitle, setSelectedMapTitle] = useState(initialSelection.title);
  const [selectedMapDefinition, setSelectedMapDefinition] = useState<MysteryRoomMapDefinition>(initialSelection.definition);
  const [editorPreviewMap, setEditorPreviewMap] = useState<MysteryRoomMapDefinition | null>(null);
  const [catalogQuery, setCatalogQuery] = useState('');
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [mapStatus, setMapStatus] = useState(initialSelection.builtin ? 'LISAD-9' : 'Карта выбрана');
  const [mapsAuthRequired, setMapsAuthRequired] = useState(false);
  const [mapsCatalogLoaded, setMapsCatalogLoaded] = useState(false);
  const requestedPreviewMap = editorPreviewMap && requestedMapId === editorPreviewMap.id ? editorPreviewMap : null;
  const requestedRegisteredMap = findMysteryRoomMapDefinition(requestedMapId);
  const effectiveSelectedMapId = requestedPreviewMap?.id ?? requestedRegisteredMap?.id ?? selectedMapId;
  const effectiveSelectedMapTitle = requestedPreviewMap?.title ?? requestedRegisteredMap?.title ?? selectedMapTitle;
  const effectiveSelectedMapDefinition = requestedPreviewMap ?? requestedRegisteredMap ?? selectedMapDefinition;
  const awaitingRequestedMap = Boolean(requestedMapId && !requestedPreviewMap && !requestedRegisteredMap && selectedMapId !== requestedMapId);
  const shouldWaitForRequestedMap = shouldWaitForRequestedMysteryRoomMap(awaitingRequestedMap);
  // Зачем: P2P-хост создаёт лобби сам, ожидание matchId не нужно.

  const filteredMaps = useMemo(() => {
    const needle = catalogQuery.trim().toLowerCase();
    if (!needle) return publishedMaps;
    return publishedMaps.filter((map) => `${map.title} ${map.description} ${map.ownerName}`.toLowerCase().includes(needle));
  }, [catalogQuery, publishedMaps]);
  const builtinMaps = useMemo(() => getBuiltinMysteryRoomMapDefinitions(), []);
  const builtinMapIds = useMemo(() => new Set(builtinMaps.map((entry) => entry.id)), [builtinMaps]);

  useEffect(() => {
    let mounted = true;
    if (!getAuthStorage()) {
      setMapsAuthRequired(true);
      setPublishedMaps([]);
      setMyMaps([]);
      setProfile(null);
      setLeaderboard([]);
      setProfileStatus('Войдите, чтобы видеть деньги, рейтинг и статистику.');
      setMyMapsStatus('Войдите, чтобы видеть свои карты.');
      setMapsCatalogLoaded(true);
      return () => {
        mounted = false;
      };
    }
    setMapsCatalogLoaded(false);
    setProfileStatus('Загружаю профиль...');
    setMyMapsStatus('Загружаю ваши карты...');
    void Promise.all([mrApiProfile(), mrApiMapsPublished(), mrApiMapsMine(), mrApiStats(6)])
      .then(([profileResponse, publishedResponse, mineResponse, statsResponse]) => {
        if (!mounted) return;
        setProfile(profileResponse.profile);
        setPublishedMaps(publishedResponse.maps ?? []);
        setMyMaps(mineResponse.maps ?? []);
        setLeaderboard(statsResponse.players ?? []);
        setProfileStatus('Профиль загружен');
        setMyMapsStatus((mineResponse.maps ?? []).length > 0 ? 'Карты загружены' : 'У вас пока нет карт.');
        setMapsAuthRequired(false);
        setMapsCatalogLoaded(true);
      })
      .catch((error: any) => {
        if (!mounted) return;
        setMapsCatalogLoaded(true);
        if (error?.status === 401) {
          setMapsAuthRequired(true);
          setProfile(null);
          setMyMaps([]);
          setLeaderboard([]);
          setProfileStatus('Войдите, чтобы видеть деньги, рейтинг и статистику.');
          setMyMapsStatus('Войдите, чтобы видеть свои карты.');
          setPublishedMaps([]);
          return;
        }
        setMapStatus(mrApiErrorMessage(error, 'Каталог карт недоступен'));
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Зачем: в P2P-режиме matchId не нужен заранее — хост создаёт лобби сам.
  // Прежний auto-redirect под Colyseus отключён (Colyseus временно закомментирован).
  // useEffect(() => {
  //   if (!playMode || !onlineMode || requestedMatchId) return;
  //   const params = new URLSearchParams(searchParams.toString());
  //   params.set('mode', 'play');
  //   params.set('net', 'colyseus');
  //   params.delete('runtime');
  //   params.set('matchId', String(generateColyseusMatchId()));
  //   router.replace(`/games/mystery-room?${params.toString()}`);
  // }, [onlineMode, playMode, requestedMatchId, router, searchParams]);

  useEffect(() => {
    let mounted = true;
    if (!getAuthStorage()) {
      setFriends([]);
      setFriendsStatus('Войдите, чтобы пригласить друзей в смену.');
      return () => {
        mounted = false;
      };
    }
    const loadFriends = () => {
      void mrApiFriends()
        .then((items) => {
          if (!mounted) return;
          setFriends(items);
          setFriendsStatus(items.length > 0 ? 'Друзья загружены' : 'Список друзей пуст. Добавьте друзей в мессенджере.');
        })
        .catch((error: any) => {
          if (!mounted) return;
          setFriends([]);
          setFriendsStatus(error?.status === 401 ? 'Войдите, чтобы пригласить друзей в смену.' : mrApiErrorMessage(error, 'Список друзей временно недоступен'));
        });
    };
    loadFriends();
    const timer = window.setInterval(loadFriends, 25000);
    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (modeParam !== 'join') return;
    const params = new URLSearchParams(searchParams.toString());
    params.set('mode', 'create');
    params.set('lobbyTab', 'find');
    router.replace(`/games/mystery-room?${params.toString()}`);
  }, [modeParam, router, searchParams]);

  const selectBuiltin = useCallback(() => {
    const builtin = getBuiltinMysteryRoomMapDefinition();
    setEditorPreviewMap(null);
    setSelectedMapId(BUILTIN_MAP_ID);
    setSelectedMapDefinition(builtin);
    setSelectedMapTitle(builtin.title);
    setMapStatus('Выбрана встроенная карта');
    setCatalogOpen(false);
  }, []);

  const selectCustomMap = useCallback(async (mapId: string) => {
    if (mapId === BUILTIN_MAP_ID) {
      selectBuiltin();
      return;
    }
    const builtinMap = findMysteryRoomMapDefinition(mapId);
    if (builtinMap) {
      setEditorPreviewMap(null);
      setSelectedMapId(mapId);
      setSelectedMapDefinition(builtinMap);
      setSelectedMapTitle(builtinMap.title);
      setMapStatus('Карта выбрана');
      setCatalogOpen(false);
      return;
    }
    setMapStatus('Загружаю карту...');
    try {
      const response = await mrApiMapGet(mapId);
      const definition = {
        ...response.map.map,
        id: response.map.id,
        title: response.map.title || response.map.map.title,
      };
      const registered = registerMysteryRoomMapDefinition(definition);
      setEditorPreviewMap(null);
      setSelectedMapId(response.map.id);
      setSelectedMapTitle(response.map.title);
      setSelectedMapDefinition(registered);
      setMapStatus('Карта выбрана');
      setCatalogOpen(false);
    } catch (error: any) {
      setMapStatus(mrApiErrorMessage(error, 'Не удалось загрузить карту'));
    }
  }, [selectBuiltin]);

  useEffect(() => {
    if (!requestedMapId) {
      return;
    }
    if (requestedPreviewMap) {
      if (selectedMapId !== requestedPreviewMap.id || selectedMapDefinition.id !== requestedPreviewMap.id) {
        setSelectedMapId(requestedPreviewMap.id);
        setSelectedMapTitle(requestedPreviewMap.title);
        setSelectedMapDefinition(requestedPreviewMap);
        setMapStatus('Тест карты');
      }
      return;
    }
    if (requestedRegisteredMap) {
      if (selectedMapId !== requestedRegisteredMap.id || selectedMapDefinition.id !== requestedRegisteredMap.id) {
        setSelectedMapId(requestedRegisteredMap.id);
        setSelectedMapTitle(requestedRegisteredMap.title);
        setSelectedMapDefinition(requestedRegisteredMap);
        if (editorPreviewMode) {
          setMapStatus('Тест карты');
        }
      }
      return;
    }
    if (requestedMapId && requestedMapId !== selectedMapId) {
      void selectCustomMap(requestedMapId);
    }
  }, [
    editorPreviewMode,
    requestedMapId,
    requestedPreviewMap,
    requestedRegisteredMap,
    selectCustomMap,
    selectedMapDefinition.id,
    selectedMapId,
  ]);

  const canResumePausedP2p = !playMode && netParam === 'p2p' && requestedMatchIdRaw.length >= 5;

  const openMainMenuClearP2p = useCallback(() => {
    try {
      if (typeof window !== 'undefined') sessionStorage.removeItem(MR_P2P_SOFT_UNMOUNT_KEY);
    } catch {
      /* */
    }
    const params = new URLSearchParams(searchParams.toString());
    params.delete('mode');
    params.delete('editor');
    params.delete('runtime');
    params.delete('play');
    params.delete('editorPreview');
    params.delete('lobbyTab');
    params.delete('lobbyAction');
    params.delete('net');
    params.delete('matchId');
    params.delete('p2pHost');
    if (effectiveSelectedMapId !== BUILTIN_MAP_ID) {
      params.set('mapId', effectiveSelectedMapId);
    } else {
      params.delete('mapId');
    }
    const query = params.toString();
    router.push(query ? `/games/mystery-room?${query}` : '/games/mystery-room');
  }, [effectiveSelectedMapId, router, searchParams]);

  /** Выход из mode=play в меню: сохранить matchId в URL для кнопки «Вернуться»; signaling-хост держится (мягкий unmount в P2PBeta). */
  const pauseToMainMenuPreserveP2p = useCallback(() => {
    try {
      if (typeof window !== 'undefined') sessionStorage.setItem(MR_P2P_SOFT_UNMOUNT_KEY, '1');
    } catch {
      /* */
    }
    const params = new URLSearchParams(searchParams.toString());
    const keep = (params.get('matchId') ?? '').trim();
    params.delete('mode');
    params.delete('editor');
    params.delete('runtime');
    params.delete('play');
    params.delete('editorPreview');
    params.delete('lobbyTab');
    params.delete('lobbyAction');
    if (keep) {
      params.set('net', 'p2p');
      params.set('matchId', keep);
      params.delete('p2pHost');
    } else {
      params.delete('net');
      params.delete('matchId');
      params.delete('p2pHost');
    }
    if (effectiveSelectedMapId !== BUILTIN_MAP_ID) {
      params.set('mapId', effectiveSelectedMapId);
    } else {
      params.delete('mapId');
    }
    const query = params.toString();
    router.push(query ? `/games/mystery-room?${query}` : '/games/mystery-room');
  }, [effectiveSelectedMapId, router, searchParams]);

  const resumeP2pSession = useCallback(() => {
    try {
      if (typeof window !== 'undefined') sessionStorage.removeItem(MR_P2P_SOFT_UNMOUNT_KEY);
    } catch {
      /* */
    }
    const params = new URLSearchParams(searchParams.toString());
    const mid = (params.get('matchId') ?? '').trim();
    if (mid.length < 5) return;
    params.set('mode', 'play');
    params.set('net', 'p2p');
    params.set('matchId', mid);
    router.push(`/games/mystery-room?${params.toString()}`);
  }, [router, searchParams]);

  const openMainMenu = openMainMenuClearP2p;

  const openMenuMode = useCallback(
    (
      mode: 'create' | 'about' | 'play' | 'editor',
      options: { online?: boolean; lobbyTab?: 'create' | 'find'; lobbyAction?: 'create' } = {},
    ) => {
      try {
        if (typeof window !== 'undefined') sessionStorage.removeItem(MR_P2P_SOFT_UNMOUNT_KEY);
      } catch {
        /* */
      }
      const params = new URLSearchParams(searchParams.toString());
      params.set('mode', mode);
      params.delete('editor');
      params.delete('runtime');
      params.delete('matchId');
      params.delete('play');
      params.delete('editorPreview');
      if (mode === 'create') {
        if (options.lobbyTab === 'find') params.set('lobbyTab', 'find');
        else params.delete('lobbyTab');
        if (options.lobbyAction) params.set('lobbyAction', options.lobbyAction);
        else params.delete('lobbyAction');
      } else {
        params.delete('lobbyTab');
        params.delete('lobbyAction');
      }
      if (options.online) {
        params.set('net', 'p2p');
      } else {
        params.delete('net');
      }
      if (!params.has('bots')) {
        params.set('bots', String(wanderBotsPreference));
      }
      if (effectiveSelectedMapId !== BUILTIN_MAP_ID) {
        params.set('mapId', effectiveSelectedMapId);
      } else {
        params.delete('mapId');
      }
      router.push(`/games/mystery-room?${params.toString()}`);
    },
    [effectiveSelectedMapId, router, searchParams, wanderBotsPreference],
  );

  const handleLobbyNavTabChange = useCallback(
    (tab: 'create' | 'find') => {
      const params = new URLSearchParams(searchParams.toString());
      params.set('mode', 'create');
      if (tab === 'find') params.set('lobbyTab', 'find');
      else params.delete('lobbyTab');
      params.delete('lobbyAction');
      router.replace(`/games/mystery-room?${params.toString()}`);
    },
    [router, searchParams],
  );

  const openFindGame = useCallback(() => openMenuMode('create', { lobbyTab: 'find' }), [openMenuMode]);
  const openCreateGame = useCallback(() => openMenuMode('create', { lobbyAction: 'create' }), [openMenuMode]);
  const openAboutGame = useCallback(() => openMenuMode('about'), [openMenuMode]);
  // Локальный быстрый старт — без сети.
  const openLocalPreview = useCallback(() => openMenuMode('play'), [openMenuMode]);
  const openExitGame = useCallback(() => router.push('/games'), [router]);

  // Старт сетевого P2P-матча из лобби: задаём опции (боты, mapId). matchId не генерируем —
  // P2P-хост сам создаст lobbyId через PHP signaling и подскажет инвайт-ссылку в HUD.
  const startOnlineLobby = useCallback(
    (options: {
      botCount?: number;
      botsOnly?: boolean;
      gameMode?: MysteryRoomGameMode;
      botDifficulty?: MysteryRoomBotDifficulty;
      globalMission?: MysteryRoomGlobalMissionKey;
      zombieWaveCount?: number;
      zombieWaveSize?: number;
      mrLinkedLobbyCode?: string | null;
      syncedMrLobby?: MrLobbySnapshotRow | null | undefined;
    }) => {
      try {
        if (typeof window !== 'undefined') sessionStorage.removeItem(MR_P2P_SOFT_UNMOUNT_KEY);
      } catch {
        /* */
      }
      const syncedPart = options.syncedMrLobby ? syncedMrLobbyToJoinOverrides(options.syncedMrLobby) : {};
      const params = new URLSearchParams();
      params.set('mode', 'play');
      params.set('net', 'p2p');

      const mapPick =
        syncedPart.mapId?.trim() || (options.syncedMrLobby?.mapId?.trim() ?? '') || effectiveSelectedMapId;
      if (mapPick && mapPick !== BUILTIN_MAP_ID) {
        params.set('mapId', mapPick);
      }

      const bc =
        typeof syncedPart.bots === 'number' && syncedPart.bots >= 0
          ? clampWanderBotCount(syncedPart.bots)
          : typeof options.botCount === 'number' && options.botCount >= 0
            ? clampWanderBotCount(options.botCount)
            : wanderBotsPreference;
      params.set('bots', String(bc));
      setWanderBotsPreference(bc);

      const botsOnlyMerged = !!(syncedPart.botsOnly ?? options.botsOnly);

      const effectiveMode = syncedPart.gameMode ?? options.gameMode ?? requestedGameMode;
      const effectiveDifficulty = syncedPart.botDifficulty ?? options.botDifficulty ?? requestedBotDifficulty;
      const effectiveMission =
        syncedPart.globalMission ?? options.globalMission ?? requestedGlobalMission;
      const effectiveWaveCount = syncedPart.zombieWaveCount ?? options.zombieWaveCount ?? requestedZombieWaveCount;
      const effectiveWaveSize = syncedPart.zombieWaveSize ?? options.zombieWaveSize ?? requestedZombieWaveSize;

      if (botsOnlyMerged) {
        params.set('botsOnly', '1');
      }
      const linked = options.mrLinkedLobbyCode?.trim();
      if (linked) {
        params.set('matchId', linked);
        params.set('p2pHost', '1');
      }
      if (effectiveMode) {
        params.set('gameMode', effectiveMode);
      }
      if (effectiveDifficulty) {
        params.set('botDifficulty', effectiveDifficulty);
      }
      if (effectiveMission) {
        params.set('globalMission', effectiveMission);
      }
      if (effectiveWaveCount !== null && effectiveWaveCount !== undefined && effectiveWaveCount > 0) {
        params.set('waveCount', String(effectiveWaveCount));
      }
      if (effectiveWaveSize !== null && effectiveWaveSize !== undefined && effectiveWaveSize > 0) {
        params.set('waveSize', String(effectiveWaveSize));
      }
      router.push(`/games/mystery-room?${params.toString()}`);
    },
    [
      effectiveSelectedMapId,
      requestedBotDifficulty,
      requestedGameMode,
      requestedGlobalMission,
      requestedZombieWaveCount,
      requestedZombieWaveSize,
      router,
      wanderBotsPreference,
    ],
  );

  const joinP2PLobbyFromMenu = useCallback(
    (raw: string, overrides?: JoinOrStartP2PMenuOverrides | null) => {
      try {
        if (typeof window !== 'undefined') sessionStorage.removeItem(MR_P2P_SOFT_UNMOUNT_KEY);
      } catch {
        /* */
      }
      const matchId = parseMysteryRoomP2pMatchIdFromUserInput(raw);
      if (!matchId) return;
      const params = new URLSearchParams();
      params.set('mode', 'play');
      params.set('net', 'p2p');
      params.set('matchId', matchId);
      const m = overrides ?? {};
      const mapPick = m.mapId?.trim() || effectiveSelectedMapId;
      if (mapPick !== BUILTIN_MAP_ID) params.set('mapId', mapPick);
      const bc =
        typeof m.bots === 'number' && m.bots >= 0 ? clampWanderBotCount(m.bots) : wanderBotsPreference;
      params.set('bots', String(bc));
      const mergedBotsOnly = !!(m.botsOnly ?? requestedBotsOnly);
      if (mergedBotsOnly) params.set('botsOnly', '1');
      const gm = m.gameMode ?? requestedGameMode;
      const bd = m.botDifficulty ?? requestedBotDifficulty;
      const ms = m.globalMission ?? requestedGlobalMission;
      if (gm) params.set('gameMode', gm);
      if (bd) params.set('botDifficulty', bd);
      if (ms) params.set('globalMission', ms);
      const wc = m.zombieWaveCount ?? requestedZombieWaveCount;
      const ws = m.zombieWaveSize ?? requestedZombieWaveSize;
      if (wc !== null && wc !== undefined && wc > 0) params.set('waveCount', String(wc));
      if (ws !== null && ws !== undefined && ws > 0) params.set('waveSize', String(ws));
      router.push(`/games/mystery-room?${params.toString()}`);
    },
    [
      effectiveSelectedMapId,
      requestedBotDifficulty,
      requestedBotsOnly,
      requestedGameMode,
      requestedGlobalMission,
      requestedZombieWaveCount,
      requestedZombieWaveSize,
      router,
      wanderBotsPreference,
    ],
  );

  const inviteFriend = useCallback(
    async (friend: MrFriend) => {
      const baseUrl = typeof window === 'undefined' ? 'https://lisad.kbsu.ru' : window.location.origin;
      const mid = requestedMatchIdRaw;
      const params = new URLSearchParams();
      if (mid) {
        params.set('mode', 'play');
        params.set('net', 'p2p');
        params.set('matchId', mid);
        if (effectiveSelectedMapId !== BUILTIN_MAP_ID) {
          params.set('mapId', effectiveSelectedMapId);
        }
        params.set('bots', String(wanderBotCount));
        if (requestedGameMode) params.set('gameMode', requestedGameMode);
        if (requestedBotDifficulty) params.set('botDifficulty', requestedBotDifficulty);
        if (requestedGlobalMission) params.set('globalMission', requestedGlobalMission);
        if (requestedZombieWaveCount !== null && requestedZombieWaveCount > 0) params.set('waveCount', String(requestedZombieWaveCount));
        if (requestedZombieWaveSize !== null && requestedZombieWaveSize > 0) params.set('waveSize', String(requestedZombieWaveSize));
      } else {
        params.set('net', 'p2p');
        if (effectiveSelectedMapId !== BUILTIN_MAP_ID) {
          params.set('mapId', effectiveSelectedMapId);
        }
        params.set('bots', String(wanderBotCount));
        if (requestedGameMode) params.set('gameMode', requestedGameMode);
        if (requestedBotDifficulty) params.set('botDifficulty', requestedBotDifficulty);
        if (requestedGlobalMission) params.set('globalMission', requestedGlobalMission);
        if (requestedZombieWaveCount !== null && requestedZombieWaveCount > 0) params.set('waveCount', String(requestedZombieWaveCount));
        if (requestedZombieWaveSize !== null && requestedZombieWaveSize > 0) params.set('waveSize', String(requestedZombieWaveSize));
      }
      const inviteUrl = `${baseUrl}/games/mystery-room?${params.toString()}`;
      const message = mid
        ? `Приглашение в Mystery Room (P2P): ${effectiveSelectedMapTitle}. Войти в лобби: ${inviteUrl}`
        : `Приглашение в Mystery Room: ${effectiveSelectedMapTitle}. Ссылка откроет меню сетевой игры; чтобы зайти к хосту, нужен код лобби из его игры (копирование в P2P). ${inviteUrl}`;
      setInviteStatusByFriendId((current) => ({ ...current, [friend.id]: 'Отправляю' }));
      try {
        await mrApiInviteFriend(friend.id, message);
        setInviteStatusByFriendId((current) => ({ ...current, [friend.id]: 'Отправлено' }));
      } catch (error: any) {
        setInviteStatusByFriendId((current) => ({
          ...current,
          [friend.id]: mrApiErrorMessage(error, 'Не удалось отправить'),
        }));
      }
    },
    [
      effectiveSelectedMapId,
      effectiveSelectedMapTitle,
      requestedBotDifficulty,
      requestedGameMode,
      requestedGlobalMission,
      requestedZombieWaveCount,
      requestedZombieWaveSize,
      requestedMatchIdRaw,
      wanderBotCount,
    ],
  );

  const openEditor = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());
    params.set('mode', 'editor');
    params.delete('net');
    params.delete('runtime');
    params.delete('matchId');
    params.delete('play');
    params.delete('editorPreview');
    params.delete('gameMode');
    params.delete('botDifficulty');
    params.delete('globalMission');
    params.delete('waveCount');
    params.delete('waveSize');
    params.delete('bots');
    params.delete('botsOnly');
    if (selectedMapId !== BUILTIN_MAP_ID) {
      params.set('mapId', selectedMapId);
    }
    router.push(`/games/mystery-room?${params.toString()}`);
  }, [router, searchParams, selectedMapId]);

  const playEditedMap = useCallback(
    (mapId: string, map: MysteryRoomMapDefinition, title: string) => {
      const registered = registerMysteryRoomMapDefinition({ ...map, id: mapId, title });
      setEditorPreviewMap(registered);
      setSelectedMapId(mapId);
      setSelectedMapDefinition(registered);
      setSelectedMapTitle(title);
      const params = new URLSearchParams(searchParams.toString());
      params.set('mode', 'play');
      params.delete('editor');
      params.delete('net');
      params.delete('runtime');
      params.delete('matchId');
      params.set('editorPreview', '1');
      params.set('mapId', mapId);
      params.delete('gameMode');
      params.delete('botDifficulty');
      params.delete('globalMission');
      params.delete('waveCount');
      params.delete('waveSize');
      params.set('bots', '0');
      params.delete('botsOnly');
      router.push(`/games/mystery-room?${params.toString()}`);
    },
    [router, searchParams],
  );

  const catalogPanel = catalogOpen ? (
    <MapCatalogPanel
      catalogQuery={catalogQuery}
      setCatalogQuery={setCatalogQuery}
      mapsAuthRequired={mapsAuthRequired}
      filteredMaps={filteredMaps}
      selectedMapId={selectedMapId}
      builtinMaps={builtinMaps}
      selectBuiltin={selectBuiltin}
      selectCustomMap={selectCustomMap}
    />
  ) : null;

  if (editorMode) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden bg-[radial-gradient(circle_at_top,#1e293b_0%,#09090b_40%,#020617_100%)]">
        <MapHeader
          selectedMapTitle={effectiveSelectedMapTitle}
          selectedMapId={effectiveSelectedMapId}
          selectedMapKind={builtinMapIds.has(effectiveSelectedMapId) ? 'builtin' : 'custom'}
          mapStatus="Редактор"
          mapsAuthRequired={mapsAuthRequired}
          catalogOpen={false}
          setCatalogOpen={setCatalogOpen}
          openEditor={openEditor}
          openMenu={openMainMenu}
        />
        <MysteryRoomMapWorkshop onPlayMap={playEditedMap} initialMapId={requestedMapId && requestedMapId !== BUILTIN_MAP_ID ? requestedMapId : null} />
      </div>
    );
  }

  if (!playMode) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden bg-[radial-gradient(circle_at_top,#1e293b_0%,#09090b_40%,#020617_100%)]">
        {catalogPanel}
        <MysteryRoomMainMenu
          view={menuView}
          selectedMapTitle={effectiveSelectedMapTitle}
          selectedMapId={effectiveSelectedMapId}
          selectedMapKind={builtinMapIds.has(effectiveSelectedMapId) ? 'builtin' : 'custom'}
          selectedMapDefinition={effectiveSelectedMapDefinition}
          mapStatus={mapStatus}
          mapsAuthRequired={mapsAuthRequired}
          mapsCatalogLoaded={mapsCatalogLoaded}
          publishedMapsCount={publishedMaps.length}
          publishedMaps={publishedMaps}
          myMaps={myMaps}
          myMapsStatus={myMapsStatus}
          profile={profile}
          profileStatus={profileStatus}
          leaderboard={leaderboard}
          friends={friends}
          friendsStatus={friendsStatus}
          inviteStatusByFriendId={inviteStatusByFriendId}
          onSelectBuiltin={selectBuiltin}
          onSelectCustomMap={(mapId) => {
            void selectCustomMap(mapId);
          }}
          onFindGame={openFindGame}
          onCreateGame={openCreateGame}
          onStartLobby={startOnlineLobby}
          onJoinP2PLobby={joinP2PLobbyFromMenu}
          onOpenAbout={openAboutGame}
          onBackHome={openMainMenu}
          canResumePausedP2p={canResumePausedP2p}
          onResumeP2pSession={resumeP2pSession}
          onOpenCatalog={() => setCatalogOpen(!catalogOpen)}
          onOpenEditor={openEditor}
          onOpenLocalPreview={openLocalPreview}
          onQuickStart={openLocalPreview}
          onInviteFriend={inviteFriend}
          onExitGame={openExitGame}
          wanderBotsPreference={wanderBotsPreference}
          onWanderBotsPreferenceChange={(n) => setWanderBotsPreference(clampWanderBotCount(n))}
          initialLobbyNavTab={initialLobbyNavTab}
          currentLobbyMode={currentLobbyMode}
          onLobbyNavTabChange={handleLobbyNavTabChange}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-[radial-gradient(circle_at_top,#1e293b_0%,#09090b_40%,#020617_100%)]">
      <MapHeader
        selectedMapTitle={effectiveSelectedMapTitle}
        selectedMapId={effectiveSelectedMapId}
        selectedMapKind={builtinMapIds.has(effectiveSelectedMapId) ? 'builtin' : 'custom'}
        mapStatus={mapStatus}
        mapsAuthRequired={mapsAuthRequired}
        catalogOpen={catalogOpen}
        setCatalogOpen={setCatalogOpen}
        openEditor={openEditor}
        openMenu={onlineMode ? pauseToMainMenuPreserveP2p : openMainMenu}
      />

      {catalogPanel}

      {shouldWaitForRequestedMap ? (
        <div className="flex min-h-0 flex-1 items-center justify-center px-6">
          <div className="w-full max-w-xl rounded-[28px] border border-site-accent/16 bg-site-bg/84 px-6 py-7 text-center shadow-[0_24px_80px_rgba(2,6,23,0.45)]">
            <div className="text-[0.7rem] uppercase tracking-[0.3em] text-site-accent/72">Mystery Room</div>
            <div className="mt-3 text-2xl font-black text-site-text">Подготовка тестовой карты</div>
            <div className="mt-3 text-sm leading-6 text-site-secondary">
              Загружаю карту для локального запуска. Встроенная лаборатория не будет открыта, пока не разрешится нужный `mapId`.
            </div>
          </div>
        </div>
      ) : (
        <>

      {editorPreviewMode && (
        <div className="border-b border-site-warning/20 bg-site-warning/10 px-4 py-2 text-sm text-site-warning">
          <div className="mx-auto flex w-full max-w-[1800px] flex-wrap items-center justify-between gap-2">
            <span className="font-semibold">Тест карты: {effectiveSelectedMapTitle}</span>
            <button
              type="button"
              onClick={openEditor}
              className="rounded-full border border-site-warning/45 bg-site-warning/15 px-4 py-2 text-xs font-black uppercase tracking-[0.12em] text-site-warning transition hover:border-site-warning/75 hover:bg-site-warning/25"
            >
              Вернуться в редактор
            </button>
          </div>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-hidden">
        {onlineMode ? (
          <MysteryRoomP2PBeta
            matchId={requestedMatchIdRaw || undefined}
            mapId={effectiveSelectedMapId}
            mapDefinition={effectiveSelectedMapDefinition}
            mapTitle={effectiveSelectedMapTitle}
            botCount={editorPreviewMode ? 0 : wanderBotCount}
            botsOnly={editorPreviewMode ? undefined : requestedBotsOnly || undefined}
            gameMode={editorPreviewMode ? undefined : requestedGameMode}
            botDifficulty={editorPreviewMode ? undefined : requestedBotDifficulty}
            globalMission={editorPreviewMode ? undefined : requestedGlobalMission}
            zombieWaveCount={editorPreviewMode ? undefined : requestedZombieWaveCount ?? undefined}
            zombieWaveSize={editorPreviewMode ? undefined : requestedZombieWaveSize ?? undefined}
            onBackToMenu={openMainMenu}
            assumeP2pHostBootstrap={assumeP2pHostBootstrap}
          />
        ) : (
          <MysteryRoomCoreSlice
            preferredSeed={challenge.seed}
            mapId={effectiveSelectedMapId}
            mapDefinition={effectiveSelectedMapDefinition}
            mapTitle={effectiveSelectedMapTitle}
            challengeTarget={challenge.target}
            challengerName={challenge.from}
            botCount={editorPreviewMode ? 0 : wanderBotCount}
            botsOnly={editorPreviewMode ? undefined : requestedBotsOnly || undefined}
            gameMode={editorPreviewMode ? undefined : requestedGameMode}
            botDifficulty={editorPreviewMode ? undefined : requestedBotDifficulty}
            globalMission={editorPreviewMode ? undefined : requestedGlobalMission}
            zombieWaveCount={editorPreviewMode ? undefined : requestedZombieWaveCount ?? undefined}
            zombieWaveSize={editorPreviewMode ? undefined : requestedZombieWaveSize ?? undefined}
          />
        )}
      </div>
        </>
      )}
    </div>
  );
}

function MapCatalogPanel({
  catalogQuery,
  setCatalogQuery,
  mapsAuthRequired,
  filteredMaps,
  selectedMapId,
  builtinMaps,
  selectBuiltin,
  selectCustomMap,
}: {
  catalogQuery: string;
  setCatalogQuery: (value: string) => void;
  mapsAuthRequired: boolean;
  filteredMaps: MrMapSummary[];
  selectedMapId: string;
  builtinMaps: MysteryRoomMapDefinition[];
  selectBuiltin: () => void;
  selectCustomMap: (mapId: string) => Promise<void>;
}) {
  return (
    <div className="border-b border-site-border bg-site-bg/95 px-4 py-3 shadow-[0_18px_40px_rgba(2,6,23,0.35)]">
      <div className="mx-auto grid w-full max-w-[1800px] gap-3 lg:grid-cols-[320px_minmax(0,1fr)]">
        <div className="space-y-3">
          <input
            value={catalogQuery}
            onChange={(event) => setCatalogQuery(event.target.value)}
            placeholder="Поиск по названию, автору, описанию"
            className="w-full rounded-[14px] border border-site-border bg-site-bg/80 px-3 py-2 text-sm text-site-text outline-none placeholder:text-site-muted focus:border-site-accent/40"
          />
          <div className="space-y-2">
            {builtinMaps.map((map) => (
              <MysteryRoomMapCard
                key={map.id}
                title={map.title}
                subtitle={`Встроенная · ${formatMysteryRoomPlayableModesShort(map)}`}
                selected={selectedMapId === map.id}
                badge={map.id === BUILTIN_MAP_ID ? 'default' : 'official'}
                tone={map.id === BUILTIN_MAP_ID ? 'cyan' : 'amber'}
                mapDefinition={map}
                onClick={map.id === BUILTIN_MAP_ID ? selectBuiltin : () => void selectCustomMap(map.id)}
              />
            ))}
          </div>
        </div>
        <div className="grid max-h-64 gap-2 overflow-y-auto sm:grid-cols-2 xl:grid-cols-3">
          {mapsAuthRequired ? (
            <div className="rounded-[16px] border border-site-warning/20 bg-site-warning/10 px-4 py-3 text-sm text-site-warning">
              Пользовательские карты доступны после входа.
            </div>
          ) : filteredMaps.length > 0 ? (
            filteredMaps.map((map) => (
              <MysteryRoomMapCard
                key={map.id}
                title={map.title}
                subtitle={`${map.ownerName || `user #${map.ownerUserId}`} · ${formatBytes(map.assetBytesTotal)}`}
                description={map.description || formatDate(map.updatedAt)}
                selected={selectedMapId === map.id}
                badge="published"
                preview={map.preview}
                onClick={() => void selectCustomMap(map.id)}
              />
            ))
          ) : (
            <div className="rounded-[16px] border border-dashed border-site-border px-4 py-3 text-sm text-site-muted">Опубликованных пользовательских карт пока нет.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function MapHeader({
  selectedMapTitle,
  selectedMapId,
  selectedMapKind,
  mapStatus,
  mapsAuthRequired,
  catalogOpen,
  setCatalogOpen,
  openEditor,
  openMenu,
}: {
  selectedMapTitle: string;
  selectedMapId: string;
  selectedMapKind?: 'builtin' | 'custom';
  mapStatus: string;
  mapsAuthRequired: boolean;
  catalogOpen: boolean;
  setCatalogOpen: (open: boolean) => void;
  openEditor: () => void;
  openMenu: () => void;
}) {
  return (
    <header className="z-20 border-b border-site-border bg-site-bg/92 px-4 py-3 backdrop-blur">
      <div className="mx-auto flex w-full max-w-[1800px] flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[0.62rem] uppercase tracking-[0.22em] text-site-muted">Лаборатория</div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="truncate text-base font-semibold text-site-text">{selectedMapTitle}</span>
            <span className="rounded-full border border-site-border bg-site-surface/[0.04] px-2 py-1 text-[0.62rem] uppercase tracking-[0.14em] text-site-secondary">
              {selectedMapKind ?? (selectedMapId === BUILTIN_MAP_ID ? 'builtin' : 'custom')}
            </span>
            {mapsAuthRequired && <span className="text-xs text-site-warning">войдите для каталога</span>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full border border-site-border bg-site-surface/[0.04] px-3 py-2 text-xs text-site-secondary">{mapStatus}</span>
          <button
            type="button"
            onClick={openMenu}
            className="rounded-full border border-site-border bg-site-surface/[0.04] px-4 py-2 text-sm font-semibold text-site-text transition hover:border-site-border"
          >
            Меню
          </button>
          <button
            type="button"
            onClick={() => setCatalogOpen(!catalogOpen)}
            className="rounded-full border border-site-accent/25 bg-site-accent/10 px-4 py-2 text-sm font-semibold text-site-accent transition hover:border-site-accent/60"
          >
            Выбрать карту
          </button>
          <button
            type="button"
            onClick={openEditor}
            className="rounded-full border border-site-warning/25 bg-site-warning/10 px-4 py-2 text-sm font-semibold text-site-warning transition hover:border-site-warning/60"
          >
            Редактор
          </button>
        </div>
      </div>
    </header>
  );
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return 'нет даты';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('ru-RU');
}
